/* eslint-disable react-hooks/exhaustive-deps */
/*
  AdminAnalytics — privacy-first visitor / user analytics dashboard.

  Tabs: Overview · Acquisition · Behaviour · Engagement · Users · Insights
  All data sourced from /api/admin/analytics/* (admin-only).
*/
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { TrendingUp, Users, Eye, MousePointerClick, Sparkles, Activity, ArrowUpRight, ArrowDownRight, Clock } from "lucide-react";

const SPANS = [
  { v: "24h", l: "24 hours" },
  { v: "7d",  l: "7 days" },
  { v: "30d", l: "30 days" },
  { v: "90d", l: "90 days" },
  { v: "365d", l: "1 year" },
];
const SUB_TABS = [
  { v: "overview",   l: "Overview" },
  { v: "acquisition", l: "Acquisition" },
  { v: "behaviour",  l: "Behaviour" },
  { v: "engagement", l: "Engagement" },
  { v: "users",      l: "Users" },
  { v: "insights",   l: "Insights" },
];
const CHART_COLORS = {
  visitors: "#A66A72",   // mauve
  signups:  "#86997B",   // sage
  revenue:  "#EFC79F",   // champagne
  primary:  "#3A2A2D",   // cocoa
  muted:    "#8A7A7D",
};

function Kpi({ label, value, hint, accent }) {
  return (
    <div data-testid={`analytics-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="ss-card p-4 sm:p-5">
      <p className="text-[10px] uppercase tracking-[0.3em] text-mauve">{label}</p>
      <p className="font-heading text-3xl sm:text-4xl text-cocoa mt-2">{value ?? "—"}</p>
      {hint && <p className="mt-1 text-xs text-cocoa/55">{hint}</p>}
      {accent}
    </div>
  );
}

function SectionTitle({ title, kicker, right }) {
  return (
    <div className="flex items-end justify-between gap-3 flex-wrap mb-3 mt-8">
      <div>
        {kicker && <p className="text-[10px] tracking-[0.35em] uppercase text-mauve">{kicker}</p>}
        <h3 className="font-heading text-xl sm:text-2xl text-cocoa">{title}</h3>
      </div>
      {right}
    </div>
  );
}

function MiniTable({ rows, valueLabel = "n", testid, fmt }) {
  if (!rows?.length) return <p className="text-cocoa/45 text-sm italic">No data yet.</p>;
  const max = Math.max(...rows.map((r) => r.n || 0)) || 1;
  return (
    <div data-testid={testid} className="ss-card overflow-hidden">
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={i ? "border-t border-champagne/40" : ""}>
              <td className="px-4 py-3 text-cocoa truncate max-w-[60%]">{fmt ? fmt(r.value) : (r.value || <span className="italic text-cocoa/40">—</span>)}</td>
              <td className="px-4 py-3 w-1/3">
                <div className="h-1.5 bg-champagne/40 rounded-full overflow-hidden">
                  <div className="h-full bg-mauve/70 rounded-full" style={{ width: `${(r.n / max) * 100}%` }} />
                </div>
              </td>
              <td className="px-4 py-3 text-right text-cocoa/70 tabular-nums">{r.n.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminAnalytics() {
  const [tab, setTab] = useState("overview");
  const [span, setSpan] = useState("7d");

  return (
    <div data-testid="analytics-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] tracking-[0.35em] uppercase text-mauve">Visitor & user analytics</p>
          <h2 className="font-heading text-2xl text-cocoa">Pattern, traffic, retention.</h2>
        </div>
        <div className="flex items-center gap-1 flex-wrap" data-testid="analytics-span-bar">
          {SPANS.map((s) => (
            <button
              key={s.v}
              data-testid={`analytics-span-${s.v}`}
              onClick={() => setSpan(s.v)}
              className={`ss-pill h-9 text-xs ${span === s.v ? "ss-pill-primary" : "ss-pill-ghost"}`}
            >
              {s.l}
            </button>
          ))}
        </div>
      </div>

      <nav className="mt-5" data-testid="analytics-subtabs">
        <div className="flex flex-wrap gap-1 sm:gap-2 border-b border-champagne">
          {SUB_TABS.map((t) => (
            <button
              key={t.v}
              data-testid={`analytics-subtab-${t.v}`}
              onClick={() => setTab(t.v)}
              className={`px-3 sm:px-4 py-2.5 text-sm border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === t.v ? "border-cocoa text-cocoa" : "border-transparent text-cocoa/55 hover:text-cocoa"}`}
            >
              {t.l}
            </button>
          ))}
        </div>
      </nav>

      <div className="mt-6">
        {tab === "overview"   && <Overview span={span} />}
        {tab === "acquisition" && <Acquisition span={span} />}
        {tab === "behaviour"  && <Behaviour span={span} />}
        {tab === "engagement" && <Engagement span={span} />}
        {tab === "users"      && <UsersTab span={span} />}
        {tab === "insights"   && <Insights span={span} />}
      </div>
    </div>
  );
}

// ─── Overview ────────────────────────────────────────────────────────────
function Overview({ span }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/admin/analytics/overview?span=${span}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [span]);
  if (!data) return <Loading />;
  if (data.error) return <ErrState />;
  const k = data.kpis;
  return (
    <div data-testid="analytics-overview">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        <Kpi label="Visitors" value={k.visitors.toLocaleString()} hint={`${k.page_views.toLocaleString()} page views`} />
        <Kpi label="Signups" value={k.signups.toLocaleString()} hint={`${k.conversion_pct}% conversion`} />
        <Kpi label="Active users" value={k.active_users.toLocaleString()} hint={`${k.logins.toLocaleString()} logins`} />
        <Kpi label="Pattern reads" value={k.asks_completed.toLocaleString()} hint={`${k.asks_submitted.toLocaleString()} submitted`} />
        <Kpi label="Paid subs" value={k.paid_subscribers.toLocaleString()} />
        <Kpi label="Visitor conv." value={`${k.conversion_pct}%`} />
      </div>

      <SectionTitle kicker="Trends" title="Visitors and signups over time" />
      <div className="ss-card p-4 sm:p-5">
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={mergeSeries(data.series.visitors_by_day, data.series.signups_by_day)}>
            <CartesianGrid stroke="#EFC79F" strokeOpacity={0.4} strokeDasharray="2 4" vertical={false} />
            <XAxis dataKey="day" stroke="#8A7A7D" fontSize={10} tickMargin={6} />
            <YAxis stroke="#8A7A7D" fontSize={10} width={28} />
            <Tooltip contentStyle={{ background: "#FEF7EB", border: "1px solid #EFC79F", fontSize: 12 }} />
            <Line type="monotone" dataKey="visitors" stroke={CHART_COLORS.visitors} strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="signups"  stroke={CHART_COLORS.signups}  strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
        <div className="flex gap-4 text-[11px] text-cocoa/60 mt-2">
          <span className="flex items-center gap-1.5"><span className="w-3 h-0.5" style={{ background: CHART_COLORS.visitors }} /> Visitors</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-0.5" style={{ background: CHART_COLORS.signups }} /> Signups</span>
        </div>
      </div>

      <SectionTitle kicker="Activity" title="Top events" />
      <MiniTable rows={data.top_events} testid="analytics-top-events" />
    </div>
  );
}

function mergeSeries(a = [], b = []) {
  const map = {};
  a.forEach((d) => { map[d.day] = { day: d.day, visitors: d.n, signups: 0 }; });
  b.forEach((d) => { map[d.day] = { ...(map[d.day] || { day: d.day, visitors: 0 }), signups: d.n }; });
  return Object.values(map).sort((x, y) => x.day.localeCompare(y.day));
}

// ─── Acquisition ─────────────────────────────────────────────────────────
function Acquisition({ span }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/admin/analytics/acquisition?span=${span}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [span]);
  if (!data) return <Loading />;
  if (data.error) return <ErrState />;
  return (
    <div data-testid="analytics-acquisition" className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-5">
        <div>
          <SectionTitle kicker="Inbound" title="Top referrers" />
          <MiniTable rows={data.referrers} testid="analytics-referrers" />
        </div>
        <div>
          <SectionTitle kicker="Pages" title="Top landing pages" />
          <MiniTable rows={data.landing_pages} testid="analytics-landing-pages" />
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-5">
        <div>
          <SectionTitle kicker="UTM" title="Source" />
          <MiniTable rows={data.utm_source} testid="analytics-utm-source" />
        </div>
        <div>
          <SectionTitle kicker="UTM" title="Medium" />
          <MiniTable rows={data.utm_medium} testid="analytics-utm-medium" />
        </div>
        <div>
          <SectionTitle kicker="UTM" title="Campaign" />
          <MiniTable rows={data.utm_campaign} testid="analytics-utm-campaign" />
        </div>
      </div>
    </div>
  );
}

// ─── Behaviour ───────────────────────────────────────────────────────────
function Behaviour({ span }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/admin/analytics/behaviour?span=${span}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [span]);
  if (!data) return <Loading />;
  if (data.error) return <ErrState />;
  return (
    <div data-testid="analytics-behaviour">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
        <Kpi label="Sessions" value={data.sessions.toLocaleString()} />
        <Kpi label="Avg depth" value={data.avg_session_depth} hint="events per session" />
        <Kpi label="Bounce" value={`${data.bounce_rate_pct}%`} hint="single-view sessions" />
      </div>
      <SectionTitle kicker="Paths" title="Top pages viewed" />
      <MiniTable rows={data.page_views_by_path} testid="analytics-page-views" />
      <SectionTitle kicker="CTAs" title="Top clicked elements" />
      <MiniTable rows={data.top_ctas} testid="analytics-top-ctas" />
    </div>
  );
}

// ─── Engagement ──────────────────────────────────────────────────────────
function Engagement({ span }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/admin/analytics/engagement?span=${span}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [span]);
  if (!data) return <Loading />;
  if (data.error) return <ErrState />;
  return (
    <div data-testid="analytics-engagement">
      <SectionTitle kicker="Funnel" title="Visitor → paid" />
      <div className="ss-card p-4 sm:p-5">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data.funnel}>
            <CartesianGrid stroke="#EFC79F" strokeOpacity={0.4} strokeDasharray="2 4" vertical={false} />
            <XAxis dataKey="stage" stroke="#8A7A7D" fontSize={11} />
            <YAxis stroke="#8A7A7D" fontSize={10} width={28} />
            <Tooltip contentStyle={{ background: "#FEF7EB", border: "1px solid #EFC79F", fontSize: 12 }} />
            <Bar dataKey="n" fill={CHART_COLORS.visitors} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <SectionTitle kicker="Features" title="What users actually use" />
      <MiniTable rows={data.feature_usage.map((f) => ({ value: f.feature, n: f.n }))} testid="analytics-feature-usage" />

      <SectionTitle kicker="Retention" title="Weekly cohorts" />
      <CohortGrid cohorts={data.cohorts} />
    </div>
  );
}

function CohortGrid({ cohorts }) {
  if (!cohorts?.length) {
    return <p className="text-cocoa/45 text-sm italic">Need at least one signup to chart cohorts.</p>;
  }
  return (
    <div data-testid="analytics-cohorts" className="ss-card p-3 overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-[9px] uppercase tracking-widest text-cocoa/55">
          <tr>
            <th className="text-left px-2 py-2">Cohort</th>
            <th className="text-right px-2 py-2">Users</th>
            {[0, 1, 2, 3, 4, 5].map((n) => <th key={n} className="text-center px-2 py-2">W{n}</th>)}
          </tr>
        </thead>
        <tbody>
          {cohorts.map((c) => (
            <tr key={c.cohort} className="border-t border-champagne/40">
              <td className="px-2 py-2 text-cocoa whitespace-nowrap">{c.cohort}</td>
              <td className="px-2 py-2 text-right text-cocoa/70">{c.size}</td>
              {c.weeks.map((w, i) => (
                <td key={i} className="px-1 py-2 text-center">
                  {w ? (
                    <span className="inline-block w-10 h-7 leading-7 rounded text-[10px] tabular-nums"
                          style={{ background: `rgba(166, 106, 114, ${0.08 + (w.pct / 100) * 0.55})`, color: w.pct > 35 ? "#FFF8F1" : "#3A2A2D" }}>
                      {w.pct}%
                    </span>
                  ) : <span className="text-cocoa/30">—</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Users ───────────────────────────────────────────────────────────────
function UsersTab({ span }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const limit = 25;
  const [active, setActive] = useState(null); // user_id selected for drilldown

  const load = async () => {
    setData(null);
    try {
      const params = new URLSearchParams({ skip: String(skip), limit: String(limit), span });
      if (q.trim()) params.set("q", q.trim());
      const r = await api.get(`/admin/analytics/users?${params.toString()}`);
      setData(r.data);
    } catch { setData({ error: true }); }
  };
  useEffect(() => { load(); }, [skip, span]);

  if (!data) return <Loading />;
  if (data.error) return <ErrState />;
  if (active) return <UserDetail userId={active} span={span} onBack={() => setActive(null)} />;

  return (
    <div data-testid="analytics-users">
      <form onSubmit={(e) => { e.preventDefault(); setSkip(0); load(); }} className="flex gap-2 mb-4">
        <input data-testid="analytics-users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email or name…"
          className="flex-1 bg-ivory border border-champagne rounded-md h-11 px-3 text-cocoa outline-none focus:border-mauve" />
        <button type="submit" className="ss-pill ss-pill-primary h-11 px-5 text-sm">Search</button>
      </form>
      <div className="ss-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-champagne/40 text-[10px] uppercase tracking-widest text-cocoa/60">
              <tr>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3 hidden sm:table-cell">Display name</th>
                <th className="text-right px-4 py-3 hidden md:table-cell">Sessions</th>
                <th className="text-right px-4 py-3 hidden md:table-cell">Asks</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Last seen</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-cocoa/50 text-sm">No users match.</td></tr>}
              {data.items.map((u) => (
                <tr key={u.id} data-testid={`analytics-user-row-${u.id}`} className="border-t border-champagne/40 hover:bg-blush/15">
                  <td className="px-4 py-3 text-cocoa break-all">
                    {u.email}
                    {u.is_ambassador && <span className="ml-2 text-[9px] uppercase tracking-widest text-mauve">amb</span>}
                  </td>
                  <td className="px-4 py-3 text-cocoa/70 hidden sm:table-cell">{u.display_name || <span className="italic text-cocoa/35">—</span>}</td>
                  <td className="px-4 py-3 text-right text-cocoa/75 tabular-nums hidden md:table-cell">{u.sessions}</td>
                  <td className="px-4 py-3 text-right text-cocoa/75 tabular-nums hidden md:table-cell">{u.asks}</td>
                  <td className="px-4 py-3 text-cocoa/55 hidden lg:table-cell">{u.last_seen ? new Date(u.last_seen).toLocaleString() : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`analytics-user-open-${u.id}`} onClick={() => setActive(u.id)} className="text-xs text-mauve hover:text-cocoa">Open →</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-champagne/40 bg-ivory text-xs">
          <span className="text-cocoa/55">{data.items.length ? skip + 1 : 0}–{skip + data.items.length} of {data.total}</span>
          <div className="flex items-center gap-2">
            <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} className="ss-pill ss-pill-ghost h-8 text-xs disabled:opacity-40">Prev</button>
            <button disabled={skip + limit >= data.total} onClick={() => setSkip(skip + limit)} className="ss-pill ss-pill-ghost h-8 text-xs disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function UserDetail({ userId, span, onBack }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/admin/analytics/user/${userId}?span=${span}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [userId, span]);
  if (!data) return <Loading />;
  if (data.error) return <ErrState />;
  const dowLabels = ["", "Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const u = data.user;
  return (
    <div data-testid={`analytics-user-detail-${userId}`}>
      <button onClick={onBack} className="text-xs text-mauve mb-3">← Back to users</button>
      <div className="ss-card-premium p-5">
        <p className="text-[10px] tracking-[0.35em] uppercase text-mauve">User</p>
        <h3 className="font-heading text-2xl text-cocoa mt-1">{u.display_name || u.email}</h3>
        <p className="text-sm text-cocoa/65">{u.email}</p>
        <div className="mt-3 flex gap-2 flex-wrap text-[11px]">
          <span className="px-2 py-1 rounded-full bg-mauve/10 text-mauve uppercase tracking-widest">{u.plan || "free"}</span>
          {u.is_ambassador && <span className="px-2 py-1 rounded-full bg-sage/30 text-cocoa uppercase tracking-widest">ambassador</span>}
          {u.role === "admin" && <span className="px-2 py-1 rounded-full bg-cocoa text-ivory uppercase tracking-widest">admin</span>}
        </div>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        <div className="ss-card p-4">
          <p className="text-[10px] uppercase tracking-[0.3em] text-mauve">Behavioural pattern</p>
          <p className="mt-2 text-cocoa text-sm leading-relaxed">
            {data.behaviour?.busiest_hour != null
              ? <>Most active around <strong className="font-heading">{String(data.behaviour.busiest_hour).padStart(2, "0")}:00 UTC</strong>, especially on <strong className="font-heading">{dowLabels[data.behaviour.busiest_dow] || "—"}</strong>.</>
              : <span className="text-cocoa/50 italic">Not enough events yet to read a pattern.</span>}
          </p>
        </div>
        <div className="ss-card p-4">
          <p className="text-[10px] uppercase tracking-[0.3em] text-mauve">Top archetypes</p>
          {data.top_archetypes?.length ? (
            <ul className="mt-2 space-y-1 text-sm text-cocoa">
              {data.top_archetypes.slice(0, 5).map((a, i) => <li key={i}>{a.value} <span className="text-cocoa/55">× {a.n}</span></li>)}
            </ul>
          ) : <p className="mt-2 text-cocoa/50 italic text-sm">None yet.</p>}
        </div>
      </div>

      <SectionTitle kicker="History" title="Event counts" />
      <MiniTable rows={data.event_counts.map((e) => ({ value: e.value, n: e.n }))} testid={`analytics-user-events-${userId}`} />

      <SectionTitle kicker="Timeline" title="Recent events" />
      <div className="ss-card overflow-hidden">
        <table className="w-full text-xs">
          <tbody>
            {data.timeline.map((t, i) => (
              <tr key={i} className={i ? "border-t border-champagne/40" : ""}>
                <td className="px-3 py-2 text-cocoa/55 whitespace-nowrap">{new Date(t.at).toLocaleString()}</td>
                <td className="px-3 py-2 text-cocoa">{t.event}</td>
                <td className="px-3 py-2 text-cocoa/55 italic">{t.path || ""}</td>
              </tr>
            ))}
            {data.timeline.length === 0 && <tr><td className="px-3 py-4 text-cocoa/45 italic text-center text-sm">No events in range.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Insights ────────────────────────────────────────────────────────────
function Insights({ span }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/admin/analytics/insights?span=${span}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [span]);
  if (!data) return <Loading />;
  if (data.error) return <ErrState />;
  const iconFor = (kind) => ({
    visitors_trend: TrendingUp, advisor_pulse: Sparkles, busiest_hour: Clock,
    top_referrer: ArrowUpRight, conversion: MousePointerClick, top_feature: Activity,
  })[kind] || Eye;
  return (
    <div data-testid="analytics-insights" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {data.cards.map((c, i) => {
        const Icon = iconFor(c.kind);
        const TrendIcon = c.direction === "up" ? ArrowUpRight : (c.direction === "down" ? ArrowDownRight : null);
        return (
          <article key={i} data-testid={`insight-card-${c.kind}`} className="ss-card-premium p-5 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full"
                 style={{ background: "radial-gradient(circle, rgba(166,106,114,0.18), transparent 65%)" }} />
            <Icon className="text-mauve" size={20} strokeWidth={1.5} />
            <h3 className="font-heading text-2xl text-cocoa mt-3 leading-tight">{c.title}</h3>
            <p className="mt-2 text-sm text-cocoa/65 leading-relaxed">{c.subtitle}</p>
            {TrendIcon && (
              <div className="mt-3 inline-flex items-center gap-1 text-[11px] text-mauve">
                <TrendIcon size={12} /> {c.delta_pct}% vs previous
              </div>
            )}
          </article>
        );
      })}
      {data.cards.length === 0 && <p className="text-cocoa/45 italic text-sm">No insights yet — once people start using the site, you'll see narrative summaries here.</p>}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────
function Loading() { return <div data-testid="analytics-loading" className="ss-card p-8 text-center text-cocoa/50 text-sm">Loading…</div>; }
function ErrState() { return <div data-testid="analytics-error" className="ss-card p-6 text-center text-rose text-sm">Couldn't load. Try a different timeframe.</div>; }
